import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { LineEditor } from "./LineEditor";
import { ApprovalRulePanel } from "./ApprovalRulePanel";
import { fmt$, newLine, today, STATUS_CLASS } from "./shared";

export const PO_CFG = {
  kind: "po", noKey: "po_no", path: "/scm/purchase-orders", label: "PO", partyKey: "supplier_id",
  doneKey: "received_qty", action: "receive", actionLabel: "RECEIVE", doneLabel: "Received", finalStatus: "received",
  dateKey: "expected_date", dateLabel: "Expected date",
};
export const SO_CFG = {
  kind: "so", noKey: "so_no", path: "/scm/sales-orders", label: "SO", partyKey: "customer",
  doneKey: "fulfilled_qty", action: "fulfill", actionLabel: "FULFILL", doneLabel: "Fulfilled", finalStatus: "fulfilled",
  dateKey: "delivery_date", dateLabel: "Delivery date",
};

const canEdit = (o, cfg) => o.status === "open" && (o[`total_${cfg.doneKey}`] || 0) === 0;
const canProcess = (o) => o.status === "open" || o.status === "partial";

export function Orders({ cfg, orders, suppliers, products, locations = [], refreshKey, onChange }) {
  const [party, setParty] = useState("");
  const [lines, setLines] = useState([newLine()]);
  const [orderDate, setOrderDate] = useState(today());
  const [otherDate, setOtherDate] = useState("");
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState({});
  const total = lines.reduce((s, l) => s + l.quantity * l.unit_price, 0);
  const isPO = cfg.kind === "po";

  const reset = () => { setLines([newLine()]); setOtherDate(""); setParty(""); setOrderDate(today()); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (lines.some((l) => !l.sku || l.quantity <= 0)) { toast.error("Every line needs a product and a quantity greater than 0"); return; }
    try {
      const payload = { [cfg.partyKey]: party, lines: lines.map(({ _id, ...rest }) => rest), order_date: orderDate, [cfg.dateKey]: otherDate || null };
      if (editId) await api.patch(`${cfg.path}/${editId}`, payload);
      else await api.post(cfg.path, payload);
      toast.success(editId ? `${cfg.label} corrected` : `${cfg.label} created`);
      reset();
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const startEdit = (o) => {
    setEditId(o.id); setParty(o[cfg.partyKey]); setOrderDate(o.order_date || today()); setOtherDate(o[cfg.dateKey] || "");
    setLines(o.lines.map((l) => ({ _id: crypto.randomUUID(), sku: l.sku, quantity: l.quantity, unit_price: l.unit_price })));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancel = async (o) => {
    if (!window.confirm(`Cancel ${o[cfg.noKey]}? This cannot be undone.`)) return;
    try {
      await api.patch(`${cfg.path}/${o.id}/cancel`);
      toast.success(`${cfg.label} cancelled`);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const partyName = (o) => (isPO ? suppliers.find((s) => s.id === o.supplier_id)?.name || o.supplier_id : o.customer);

  return (
    <div>
      {isPO && <ApprovalRulePanel refreshKey={refreshKey} />}
      <form onSubmit={submit} className={`p-5 border-b border-zinc-950 space-y-3 ${editId ? "bg-amber-50/40" : ""}`} data-testid={`${cfg.kind}-form`}>
        {editId && <div className="text-[10px] font-mono text-amber-700" data-testid={`${cfg.kind}-edit-banner`}>CORRECTING {orders.find((o) => o.id === editId)?.[cfg.noKey]} — changes replace the original lines</div>}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {isPO ? (
            <select value={party} onChange={(e) => setParty(e.target.value)} required data-testid="po-supplier-select" className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none">
              <option value="">— select supplier —</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          ) : (
            <input value={party} onChange={(e) => setParty(e.target.value)} required placeholder="Customer" data-testid="so-customer-input" className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none" />
          )}
          <label className="flex flex-col">
            <span className="label-sm text-zinc-500">Order date</span>
            <input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} required data-testid={`${cfg.kind}-order-date`} className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm outline-none tnum" />
          </label>
          <label className="flex flex-col">
            <span className="label-sm text-zinc-500">{cfg.dateLabel}</span>
            <input type="date" value={otherDate} onChange={(e) => setOtherDate(e.target.value)} data-testid={`${cfg.kind}-other-date`} className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm outline-none tnum" />
          </label>
        </div>
        <LineEditor products={products} lines={lines} onChange={setLines} />
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => setLines([...lines, newLine()])} className="text-xs font-mono text-zinc-500 hover:text-zinc-950" data-testid={`${cfg.kind}-add-line`}>+ ADD LINE</button>
          <div className="flex items-center gap-4 text-xs">
            <span className="tnum">TOTAL {fmt$(total)}</span>
            {editId && <button type="button" onClick={reset} className="text-xs font-mono text-zinc-500 hover:text-zinc-950" data-testid={`${cfg.kind}-discard-edit`}>DISCARD</button>}
            <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2 hover:bg-[#FF3E00]" data-testid={`${cfg.kind}-submit-btn`}>{editId ? `SAVE ${cfg.label}` : `CREATE ${cfg.label}`}</button>
          </div>
        </div>
      </form>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="w-8" />
            <th className="text-left px-4 py-2.5">{cfg.label} #</th>
            <th className="text-left px-4 py-2.5">Date</th>
            <th className="text-left px-4 py-2.5">{isPO ? "Supplier" : "Customer"}</th>
            <th className="text-right px-4 py-2.5">Qty</th>
            <th className="text-right px-4 py-2.5">{cfg.doneLabel}</th>
            <th className="text-right px-4 py-2.5">Total</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="w-44" />
          </tr>
        </thead>
        <tbody data-testid={`${cfg.kind}s-tbody`}>
          {orders.map((o) => {
            const isOpen = !!open[o.id];
            return [
              <tr key={o.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`${cfg.kind}-row-${o.id}`}>
                <td className="px-2 py-2.5">
                  <button onClick={() => setOpen((s) => ({ ...s, [o.id]: !isOpen }))} className="text-zinc-500 hover:text-zinc-950" data-testid={`${cfg.kind}-toggle-${o.id}`}>
                    {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                  </button>
                </td>
                <td className="px-4 py-2.5 tnum text-xs">{o[cfg.noKey]}</td>
                <td className="px-4 py-2.5 tnum text-xs text-zinc-600" data-testid={`${cfg.kind}-date-${o.id}`}>{o.order_date || (o.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-2.5">{partyName(o)}</td>
                <td className="px-4 py-2.5 text-right tnum" data-testid={`${cfg.kind}-qty-${o.id}`}>{o.total_qty}</td>
                <td className="px-4 py-2.5 text-right tnum text-zinc-600">{o[`total_${cfg.doneKey}`] || 0}</td>
                <td className="px-4 py-2.5 text-right tnum font-semibold">{fmt$(o.total)}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-[10px] px-1.5 py-0.5 ${STATUS_CLASS[o.status] || ""}`} data-testid={`${cfg.kind}-status-${o.id}`}>{o.status?.toUpperCase()}</span>
                  {o.approval_status && o.approval_status !== "approved" && (
                    <Link to="/approvals" className="ml-1 text-[10px] px-1.5 py-0.5 bg-violet-50 text-violet-700 hover:underline" data-testid={`${cfg.kind}-approval-${o.id}`}>APPROVAL {o.approval_status.toUpperCase()}</Link>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-3 text-[10px] font-mono">
                    {canProcess(o) && <button onClick={() => setOpen((s) => ({ ...s, [o.id]: true }))} className="text-emerald-700 hover:underline" data-testid={`${cfg.kind}-${cfg.action}-${o.id}`}>{cfg.actionLabel}</button>}
                    {canEdit(o, cfg) && <button onClick={() => startEdit(o)} className="text-zinc-600 hover:underline" data-testid={`${cfg.kind}-edit-${o.id}`}>CORRECT</button>}
                    {canProcess(o) && <button onClick={() => cancel(o)} className="text-[#D0021B] hover:underline" data-testid={`${cfg.kind}-cancel-${o.id}`}>CANCEL</button>}
                  </div>
                </td>
              </tr>,
              isOpen && (
                <tr key={`${o.id}-lines`} className="border-b border-zinc-200 bg-zinc-50/60">
                  <td colSpan={9} className="px-6 py-3">
                    <LinesPanel cfg={cfg} order={o} products={products} locations={locations} onChange={onChange} />
                  </td>
                </tr>
              ),
            ];
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function LinesPanel({ cfg, order, products, locations, onChange }) {
  const isSO = cfg.kind === "so";
  const activeLocs = locations.filter((l) => l.active !== false);
  const [locId, setLocId] = useState(() => activeLocs.find((l) => l.is_default)?.id || activeLocs[0]?.id || "");
  const [levels, setLevels] = useState(null);
  useEffect(() => { if (isSO) api.get("/scm/stock-levels").then((r) => setLevels(r.data.levels)).catch(() => {}); }, [isSO, order.id]);
  const stockOf = (sku) => products.find((p) => p.sku === sku)?.stock ?? 0;
  const atLoc = (sku) => (levels ? levels.find((r) => r.sku === sku)?.by_location?.[locId] ?? 0 : stockOf(sku));
  const initial = () => Object.fromEntries(order.lines.map((l) => [l.sku, isSO ? Math.min(l.remaining, stockOf(l.sku)) : l.remaining]));
  const [qty, setQty] = useState(initial);
  const active = canProcess(order);

  const post = async () => {
    const lines = Object.entries(qty).map(([sku, q]) => ({ sku, quantity: Number(q) || 0 })).filter((l) => l.quantity > 0);
    if (!lines.length) { toast.error("Enter a quantity to process"); return; }
    try {
      const { data } = await api.patch(`${cfg.path}/${order.id}/${cfg.action}`, { lines, location_id: locId || null });
      toast.success(`${cfg.label} ${data.status === cfg.finalStatus ? `fully ${data.status}` : `partially ${cfg.action === "receive" ? "received" : "fulfilled"}`}${data.grn_no ? ` · ${data.grn_no}` : ""}`);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`${cfg.kind}-lines-${order.id}`}>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] tracking-wider uppercase text-zinc-500">
            <th className="text-left py-1">SKU</th>
            <th className="text-right py-1">Ordered</th>
            <th className="text-right py-1">Unit Price</th>
            <th className="text-right py-1">{cfg.doneLabel}</th>
            <th className="text-right py-1">Remaining</th>
            {isSO && <th className="text-right py-1">In stock{levels ? " @ location" : ""}</th>}
            {active && <th className="text-right py-1 w-28">{cfg.actionLabel} now</th>}
          </tr>
        </thead>
        <tbody>
          {order.lines.map((l) => (
            <tr key={l.sku} className="border-t border-zinc-200" data-testid={`${cfg.kind}-line-${order.id}-${l.sku}`}>
              <td className="py-1.5 tnum">{l.sku} <span className="text-zinc-500">· {products.find((p) => p.sku === l.sku)?.name || ""}</span></td>
              <td className="py-1.5 text-right tnum">{l.quantity}</td>
              <td className="py-1.5 text-right tnum">{fmt$(l.unit_price)}</td>
              <td className="py-1.5 text-right tnum">{l[cfg.doneKey] || 0}</td>
              <td className="py-1.5 text-right tnum font-semibold">{l.remaining}</td>
              {isSO && <td className={`py-1.5 text-right tnum ${atLoc(l.sku) < l.remaining ? "text-[#D0021B]" : "text-zinc-600"}`} data-testid={`so-atloc-${order.id}-${l.sku}`}>{atLoc(l.sku)}<span className="text-zinc-400"> / {stockOf(l.sku)}</span></td>}
              {active && (
                <td className="py-1 text-right">
                  <input type="number" min="0" max={l.remaining} value={qty[l.sku] ?? 0} onChange={(e) => setQty((q) => ({ ...q, [l.sku]: e.target.value }))} disabled={l.remaining === 0}
                    className="border border-zinc-300 focus:border-zinc-950 px-2 py-1 text-xs text-right tnum outline-none w-24 disabled:opacity-40" data-testid={`${cfg.kind}-${cfg.action}-qty-${order.id}-${l.sku}`} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {active && (
        <div className="flex flex-wrap items-center justify-end gap-3 mt-2">
          <span className="text-[10px] font-mono text-zinc-500">Partial quantities allowed — the {cfg.label} stays PARTIAL until every line is complete</span>
          {activeLocs.length > 0 && (
            <label className="flex items-center gap-1 text-[10px] font-mono text-zinc-600">
              {isSO ? "SHIP FROM" : "RECEIVE INTO"}
              <select value={locId} onChange={(e) => setLocId(e.target.value)} className="border border-zinc-300 focus:border-zinc-950 px-2 py-1 text-xs outline-none bg-white" data-testid={`${cfg.kind}-location-select-${order.id}`}>
                {activeLocs.map((l) => <option key={l.id} value={l.id}>{`${l.code} · ${l.name}`}</option>)}
              </select>
            </label>
          )}
          <button onClick={post} className="bg-zinc-950 text-white text-[10px] font-semibold px-4 py-1.5 hover:bg-[#FF3E00]" data-testid={`${cfg.kind}-${cfg.action}-post-${order.id}`}>POST {cfg.action === "receive" ? "RECEIPT" : "FULFILMENT"}</button>
        </div>
      )}
    </div>
  );
}
